import type {CopilotSpace, CopilotSpaceId, CopilotSpaceCategory} from '@github-ui/copilot-chat-types'
import {
  COPILOT_SPACES_COUNT_QUERY_KEY,
  COPILOT_SPACES_QUERY_KEY,
  copilotSpacesCategoryQueryKey,
  copilotSpacesQueryKey,
} from '../utils/fetch'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {customCopilotApiPath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import {resourceMetadataToRailsPayload} from '../utils/payload-mapper'

/**
 * The input type for the upsertCopilotSpace mutation.
 * We only allow a subset of fields to be updated from the client.
 */
export type Input = Partial<Pick<CopilotSpace, 'name' | 'resources'>> & {
  owner_id?: number
  owner_type?: string
}

export const useUpsertCopilotSpace = (id?: CopilotSpaceId) => {
  const queryClient = useQueryClient()

  const {mutateAsync: upsertCopilotSpace, ...rest} = useMutation({
    onSuccess: async () => {
      // refetch the index of spaces in the background but still wait for the specific space to be invalidated.
      void queryClient.invalidateQueries({queryKey: COPILOT_SPACES_QUERY_KEY})
      if (id) {
        await queryClient.invalidateQueries({queryKey: copilotSpacesQueryKey(id)})
      }

      const categories: CopilotSpaceCategory[] = ['starred', 'user_owned', 'org_owned', 'shared_with_user']
      await Promise.all(
        categories.map(category => queryClient.invalidateQueries({queryKey: copilotSpacesCategoryQueryKey(category)})),
      )

      await queryClient.invalidateQueries({queryKey: COPILOT_SPACES_COUNT_QUERY_KEY})
    },
    mutationFn: async (input: Input) => {
      const url = id ? customCopilotApiPath(id) : '/custom_copilots'
      const method = id ? 'PUT' : 'POST'

      const body = {
        custom_copilot: {
          name: input.name,
          owner_id: input.owner_id,
          owner_type: input.owner_type,
          resources_attributes: input.resources?.map(r => ({
            id: r.databaseId,
            copilot_chat_attachment_id: r.copilotChatAttachmentId,
            resource_type: r.type,
            // The _destroy param is a special param used by Rails' accepts_nested_attributes_for. If set to true
            // the record will be deleted.
            _destroy: r.markedForDestroy,
            metadata: resourceMetadataToRailsPayload(r),
          })),
        },
      }

      const response = await verifiedFetchJSON(url, {
        method,
        body,
      })

      const data = await response.json()

      if (!response.ok) {
        throw data.errorMessages
      }

      return data as CopilotSpace
    },
  })

  return {
    upsertCopilotSpace,
    ...rest,
  }
}
